import { useState } from "react";
import { Lightbulb } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { NumberInput } from "../../../components/ui/number-input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Badge } from "../../../components/ui/badge";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "../../../components/ui/select";
import { CAMPAIGN_GOALS, KPI_TYPES, IAB_CATEGORIES } from "../constants";

export function OverviewStep({ form, updateField, onGetRecommendations }) {
  const [categorySearch, setCategorySearch] = useState("");
  const [expandedCategories, setExpandedCategories] = useState([]);
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900 mb-1">Campaign Overview</h2>
        <p className="text-sm text-slate-500">Define your campaign goals and target audience</p>
      </div>

      {/* Campaign Name */}
      <div className="space-y-2">
        <Label className="text-slate-600">Campaign Name *</Label>
        <Input
          value={form.name}
          onChange={(e) => updateField("name", e.target.value)}
          placeholder="Enter campaign name"
          className="surface-secondary border-slate-200 text-slate-900"
          data-testid="campaign-name-input"
        />
      </div>

      {/* IAB Categories */}
      <div className="space-y-2">
        <Label className="text-slate-600">IAB Categories *</Label>
        <div className="space-y-2">
          {/* Search and Select */}
          <div className="flex gap-2">
            <Input
              placeholder="Search categories (e.g., Sports, Technology)..."
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
              className="surface-secondary border-slate-200 text-slate-900 flex-1"
            />
          </div>
          
          {/* Category List */}
          <div className="border border-slate-200 rounded-lg max-h-[250px] overflow-y-auto bg-white">
            {(() => {
              const searchLower = categorySearch.toLowerCase();
              const filteredCategories = IAB_CATEGORIES.filter(cat => 
                cat.code.toLowerCase().includes(searchLower) || 
                cat.name.toLowerCase().includes(searchLower)
              );
              
              // Group by parent category
              const parentCategories = filteredCategories.filter(c => !c.parent);
              
              return parentCategories.map(parent => {
                const children = filteredCategories.filter(c => c.parent === parent.code);
                const isExpanded = expandedCategories.includes(parent.code) || categorySearch.length > 0;
                const isSelected = form.iab_categories.includes(parent.code);
                
                return (
                  <div key={parent.code} className="border-b border-slate-100 last:border-b-0">
                    <div 
                      className={`flex items-center justify-between p-2 cursor-pointer hover:bg-slate-50 ${isSelected ? 'bg-[#3B82F6]/10' : ''}`}
                    >
                      <div className="flex items-center gap-2 flex-1" onClick={() => {
                        if (children.length > 0) {
                          setExpandedCategories(prev => 
                            prev.includes(parent.code) 
                              ? prev.filter(c => c !== parent.code)
                              : [...prev, parent.code]
                          );
                        }
                      }}>
                        {children.length > 0 && (
                          <span className="text-slate-400 text-xs w-4">
                            {isExpanded ? '▼' : '▶'}
                          </span>
                        )}
                        {children.length === 0 && <span className="w-4" />}
                        <span className="text-sm font-medium text-slate-900">{parent.code}</span>
                        <span className="text-sm text-slate-600">{parent.name}</span>
                        {children.length > 0 && (
                          <span className="text-xs text-slate-400">({children.length})</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!form.iab_categories.includes(parent.code)) {
                            updateField("iab_categories", [...form.iab_categories, parent.code]);
                          }
                        }}
                        disabled={isSelected}
                        className={`px-2 py-1 text-xs rounded ${isSelected ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#3B82F6]/10 text-[#3B82F6] hover:bg-[#3B82F6]/20'}`}
                      >
                        {isSelected ? '✓ Added' : '+ Add'}
                      </button>
                    </div>
                    {isExpanded && children.length > 0 && (
                      <div className="pl-6 bg-slate-50/50">
                        {children.map(child => {
                          const childSelected = form.iab_categories.includes(child.code);
                          return (
                            <div 
                              key={child.code}
                              className={`flex items-center justify-between p-2 hover:bg-slate-100 ${childSelected ? 'bg-[#3B82F6]/10' : ''}`}
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500">{child.code}</span>
                                <span className="text-sm text-slate-700">{child.name}</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  if (!form.iab_categories.includes(child.code)) {
                                    updateField("iab_categories", [...form.iab_categories, child.code]);
                                  }
                                }}
                                disabled={childSelected}
                                className={`px-2 py-1 text-xs rounded ${childSelected ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#3B82F6]/10 text-[#3B82F6] hover:bg-[#3B82F6]/20'}`}
                              >
                                {childSelected ? '✓' : '+'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </div>
        
        {/* Selected Categories */}
        {form.iab_categories.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500">Selected ({form.iab_categories.length})</span>
              <button 
                type="button"
                onClick={() => updateField("iab_categories", [])}
                className="text-xs text-[#EF4444] hover:underline"
              >
                Clear all
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.iab_categories.map((code) => (
                <Badge key={code} variant="secondary" className="bg-[#3B82F6]/20 text-[#3B82F6]">
                  {code} - {IAB_CATEGORIES.find(c => c.code === code)?.name || code}
                  <button 
                    type="button"
                    onClick={() => updateField("iab_categories", form.iab_categories.filter(c => c !== code))} 
                    className="ml-2 hover:text-[#EF4444]"
                  >
                    ×
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Primary Goal */}
      <div className="space-y-2">
        <Label className="text-slate-600">Primary Goal *</Label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {CAMPAIGN_GOALS.map((goal) => (
            <div
              key={goal.value}
              onClick={() => updateField("primary_goal", goal.value)}
              className={`p-4 rounded-lg cursor-pointer border transition-all ${
                form.primary_goal === goal.value
                  ? "bg-[#3B82F6]/20 border-[#3B82F6]"
                  : "surface-secondary border-slate-200 hover:border-[#3B82F6]/50"
              }`}
              data-testid={`goal-${goal.value}`}
            >
              <p className="text-sm font-medium text-slate-900">{goal.label}</p>
              <p className="text-xs text-slate-500 mt-1">{goal.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* KPI Settings */}
      <Card className="surface-primary border-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-slate-900">Key Performance Indicator</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-600">KPI Type</Label>
              <Select value={form.kpi_type} onValueChange={(v) => updateField("kpi_type", v)}>
                <SelectTrigger className="surface-secondary border-slate-200 text-slate-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="surface-primary border-slate-200">
                  {KPI_TYPES.map((kpi) => (
                    <SelectItem key={kpi.value} value={kpi.value} className="text-slate-900">
                      {kpi.label} - {kpi.desc}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-600">Target {form.kpi_type.toUpperCase()}</Label>
              <NumberInput
                value={form.kpi_target}
                onChange={(e) => {
                  const val = e.target.value;
                  updateField("kpi_target", val === "" ? 0 : (typeof val === 'number' ? val : parseFloat(val) || 0));
                }}
                className="surface-secondary border-slate-200 text-slate-900"
                min={0}
                data-testid="kpi-target-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Campaign Description */}
      <div className="space-y-2">
        <Label className="text-slate-600">Campaign Description</Label>
        <Textarea
          value={form.description}
          onChange={(e) => updateField("description", e.target.value)}
          placeholder="Internal notes about this campaign"
          className="surface-secondary border-slate-200 text-slate-900 min-h-[100px]"
        />
      </div>

      {/* Strategy Recommendations */}
      <Button
        variant="outline"
        onClick={onGetRecommendations}
        className="border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10"
      >
        <Lightbulb className="w-4 h-4 mr-2" />
        Get Strategy Recommendations
      </Button>
    </div>
  );
}
